import { type FC, useState } from "react";

import { CourseSelectionList } from "./CourseSelectionList";
import { MoveCoursesConfirmationModal } from "./MoveCoursesConfirmationModal";

import { serverAddCoursesToGrouping } from "api/api-server-actions";
import { t } from "i18n/i18n";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { store } from "store/index";
import { courseSelectors, selectCourseIdsExceptGroupingId } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";

type MoveCoursesToGroupingModalProps = {
  groupingId: string;
  onClose: () => void;
};

export const MoveCoursesToGroupingModal: FC<MoveCoursesToGroupingModalProps> = ({
  groupingId,
  onClose,
}) => {
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [confirmationCourseIds, setConfirmationCourseIds] = useState<string[]>([]);
  const courseIds = useListSelector((s) => selectCourseIdsExceptGroupingId(s, groupingId));

  const toggleCourseChecked = (courseId: string) => {
    const newSelectedIds = [...selectedCourseIds];

    const codeIndex = newSelectedIds.indexOf(courseId);
    if (codeIndex > -1) {
      newSelectedIds.splice(codeIndex, 1);
    } else {
      newSelectedIds.push(courseId);
    }

    setSelectedCourseIds(newSelectedIds);
  };

  const handleSubmit = () => {
    if (selectedCourseIds.length === 0) {
      return;
    }

    const selectedCourseIdSet = new Set(selectedCourseIds);
    const coursesFromOtherGroupings = courseSelectors
      .selectAll(store.getState())
      .filter(
        (course) =>
          selectedCourseIdSet.has(course.id) &&
          course.grouping_id !== null &&
          course.grouping_id !== groupingId,
      );

    if (coursesFromOtherGroupings.length > 0) {
      setConfirmationCourseIds(coursesFromOtherGroupings.map((course) => course.id));
      return;
    }

    handleMoveCourses();
  };

  const handleMoveCourses = async () => {
    await serverAddCoursesToGrouping(groupingId, selectedCourseIds);
    toastSuccessMessage(t("home.toasts.course_moved", { count: selectedCourseIds.length }));
    onClose();
  };

  if (confirmationCourseIds.length > 0) {
    return (
      <MoveCoursesConfirmationModal
        courseIds={confirmationCourseIds}
        onCancel={() => setConfirmationCourseIds([])}
        onConfirm={handleMoveCourses}
      />
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-2">
        <CourseSelectionList
          courseIds={courseIds}
          selectedCourseIds={selectedCourseIds}
          onCourseToggle={toggleCourseChecked}
        />
      </div>

      <div className="mt-8 flex w-full justify-end gap-2">
        <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </SquareButton>

        <SquareButton
          disabled={selectedCourseIds.length === 0}
          kind="accent"
          size="m"
          title={t("home.courses.move_selected_courses")}
          onClick={handleSubmit}
        >
          {t("home.courses.move_selected_courses")}
        </SquareButton>
      </div>
    </div>
  );
};
